'use client';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';

/* eslint-disable @next/next/no-img-element */
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { MixpanelEvent } from '@/services/eventEnums';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { MixpanelTracking } from '@/services/mixpanel';

import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { nameRegex } from '@/constants';
import customToaster from '@/components/toast/CustomToaster';
import { getCookie } from 'cookies-next';
import { submitMobileIntroForm } from '@/network/serverApis/mobileIntro';
import { triggerEvent } from '@/lib/events';
import { useSpinnerContext } from '@/context/SpinnerContext';

export default function MobileIntroForm({
  showContentComponent,
  setShowContentComponent,
  dialogFlowRef,
  setOpen,
  userRole,
  allSteps,
  setUserRole,
}: {
  setUserRole: any;
  showContentComponent: any;
  dialogFlowRef: any;
  setShowContentComponent: any;
  setOpen: any;
  userRole: any;
  allSteps: any;
}) {
  const { t } = useTranslation('cart');

  const { handleSubmit, control, setValue, reset, getValues } = useForm<any>();
  const { startLoaderEvent, stopLoaderEvent } = useSpinnerContext();

  const onSubmit = async (data: any) => {
    const firstName = data?.firstName?.trim();
    const lastName = data?.lastName?.trim();

    const hubspotutk = getCookie('hubspotutk');
    const redirect = window.location.href;

    const context = {
      hutk: hubspotutk,
      pageName: document.title,
      pageUri: redirect,
    };

    const formData = {
      firstName,
      lastName,
      email: data?.email,
      company: data?.company,
      description: data?.description,
      phone: data?.phone,
      redirect,
      role: userRole ?? 'Other',
      hubspotutk,
      context,
    };
    try {
      triggerEvent(startLoaderEvent);
      // eslint-disable-next-line require-use-server
      const mobileIntroFormData = await submitMobileIntroForm(formData);
      if (mobileIntroFormData?.data) {
        customToaster(`${t('requestSubmitted', 'Request submitted successfully!')}`, 'success');
        setOpen(false);
        MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_MOBILE_INTRO_FORM_SUBMISSION, {
          firstName,
          lastName,
          email: data?.email,
          phone: data?.phone,
          organization: data?.company,
          role: userRole,
          description: data?.description,
          success: true,
        });
      }
    } catch (err: any) {
      MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_MOBILE_INTRO_FORM_SUBMISSION, {
        firstName,
        lastName,
        email: data?.email,
        phone: data?.phone,
        organization: data?.company,
        role: userRole,
        description: data?.description,
        success: false,
      });
      customToaster(`${t('requestCannotExecute', 'The request could not be executed.')}`);
    } finally {
      triggerEvent(stopLoaderEvent);
    }
  };

  const onError = (e: any) => {
    const error =
      e?.firstName?.message ||
      e?.lastName?.message ||
      e?.company?.message ||
      `${t('completeFieldsError', 'Please fill all required fields.')}`;
    const formData = getValues();
    const mixpanelAccountCreationFailData = {
      first_name: formData?.firstName,
      last_name: formData?.lastName,
      country: formData?.country,
      organization: formData?.organization,
      industry: formData?.industry,
      email_address: formData?.email,
      failure_reason: error,
    };
    return customToaster(error);
  };
  return (
    <>
      <IconButton
        name="back"
        aria-label="close-delete-item-dialog"
        className="close-btn"
        onClick={() => {
          if (dialogFlowRef?.current === 'org_img') {
            setUserRole('');
          }
          setShowContentComponent(
            dialogFlowRef?.current === 'explore' ? allSteps['explore'] : allSteps['org_img']
          );
        }}
        sx={{
          position: 'absolute',
          top: 5,
          left: 8,
        }}
      >
        <ArrowBackOutlinedIcon />
      </IconButton>
      <DialogTitle
        sx={{ margin: '1rem 0px 0px 0px', p: '2', textAlign: 'center' }}
        id="customized-dialog-title"
      >
        {t('freeConsultation', 'Get a free consultation with a imagery expert')}
      </DialogTitle>
      <IconButton
        name="Close Delete Item"
        aria-label="close-delete-item-dialog"
        className="close-btn"
        onClick={() => {
          setOpen(false);
          MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_MOBILE_INTRO_DIALOG_CLOSE);
        }}
        sx={{
          position: 'absolute',
          right: 8,
          top: 5,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        className="mbl-intro-dialog-content"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">
          {t(
            'connectWithSomeone',
            'We&apos;ll connect you with someone who can help with your specific needs'
          )}
        </Typography>

        <form className="mble-intro-form" onSubmit={handleSubmit(onSubmit, onError)}>
          <Box className="mble-intro-form-content">
            <Controller
              control={control}
              name="firstName"
              rules={{
                required: true,
                pattern: { value: nameRegex, message: t('invalidFirstName') },
              }}
              render={({ field }) => {
                return (
                  <TextField
                    id="filled-basic"
                    fullWidth
                    label={t('firstName', 'First Name')}
                    placeholder="John"
                    required
                    variant="filled"
                    {...field}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                    }}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="lastName"
              rules={{
                required: true,
                pattern: { value: nameRegex, message: t('invalidLastName') },
              }}
              render={({ field }) => {
                return (
                  <TextField
                    id="filled-basic"
                    fullWidth
                    label={t('lastName', 'Last Name')}
                    required
                    placeholder="Smith"
                    variant="filled"
                    {...field}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                    }}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="email"
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <TextField
                    id="filled-basic"
                    fullWidth
                    required
                    label={t('email', 'Email')}
                    placeholder="john@company.com"
                    variant="filled"
                    type="email"
                    {...field}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="company"
              rules={{
                required: true,
                minLength: { value: 2, message: t('invalidOrganization') },
              }}
              render={({ field }) => {
                return (
                  <TextField
                    id="filled-basic"
                    fullWidth
                    label={t('company', 'Company')}
                    required
                    placeholder="Acme Corp"
                    variant="filled"
                    {...field}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                    }}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="description"
              render={({ field }) => {
                return (
                  <TextField
                    id="filled-multiline-flexible"
                    fullWidth
                    label={t('description', 'Description')}
                    required
                    multiline
                    //   maxRows={3}
                    placeholder={t(
                      'tellUsYourProject',
                      'Tell us about project or requirements ...'
                    )}
                    variant="filled"
                    {...field}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="phone"
              render={({ field }) => {
                return (
                  <TextField
                    sx={{ marginBottom: '10px' }}
                    id="filled-basic"
                    fullWidth
                    type="tel"
                    label={t('phone', 'Phone (Optional)')}
                    placeholder="+1(555) 000-0000"
                    variant="filled"
                    {...field}
                  />
                );
              }}
            />
          </Box>
          <Box className="mble-intro-form-footer">
            <Button fullWidth variant="contained" type="submit" color="primary">
              {t('submit', 'Submit')}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </>
  );
}
